import React from 'react';
import { Button, Backdrop, Tabs, Tab, Box, Typography } from '@material-ui/core';
import NoSleep from 'nosleep.js';
import String from './String';
import Map from './Map';
import { MAPBOX_DARK_MAP_STYLE, MAPBOX_LIGHT_MAP_STYLE } from '../../config/map';
import { configHasGeoSpots, getMapSpotsFromConfig } from '../utils/geoXp';
import { getBounds, getOverviewFrame, isAreaTooWide } from '../utils/map';
import Layout from './Layout';
import Header from './Header';
import Footer from './Footer';
import ThemeSwitcher from './ThemeSwitcher';
import { downloadAudio, downloadCredits, downloadVideo } from '../utils/offline';
import { sleep } from '../utils/utils';
import { useSession } from '../contexts/session';
import { useTheme } from '../contexts/theme';
import { useLanguage } from '../contexts/language';
import { useFocusRestore } from '../hooks/useFocusRestore';

const noSleep = new NoSleep();

function DownloadMapTilesHiddenOverlay({ experience, loaded, onProgress }) {
  const [lightLoaded, setLightLoaded] = React.useState(false);
  const [darkLoaded, setDarkLoaded] = React.useState(false);
  // both themes sweep in parallel; combine their step counts into one figure
  const [lightProgress, setLightProgress] = React.useState({ done: 0, total: 0 });
  const [darkProgress, setDarkProgress] = React.useState({ done: 0, total: 0 });

  const mapSpots = React.useMemo(
    () => (experience?.config ? getMapSpotsFromConfig(experience.config) : []),
    [experience]
  );

  React.useEffect(() => {
    if (lightLoaded && darkLoaded && loaded) {
      console.info('Map tiles cached successfully');
      loaded();
    }
  }, [lightLoaded, darkLoaded, loaded]);

  React.useEffect(() => {
    const total = lightProgress.total + darkProgress.total;
    const done = lightProgress.done + darkProgress.done;
    if (total > 0) onProgress?.(done, total);
  }, [lightProgress, darkProgress, onProgress]);

  return (
    <Backdrop style={{ position: 'fixed', opacity: 0, pointerEvents: 'none' }} open aria-hidden="true">
      <Layout
        fullscreen
        top={<Header />}
        bottom={
          <Footer>
            <ThemeSwitcher />
          </Footer>
        }
      >
        <Tabs value="map" style={{ width: '100%', display: 'flex' }}>
          <Tab label="Map" value="map" style={{ flex: 1 }} />
        </Tabs>
        <Layout>
          <Box width="100%" height="100%" position="relative">
            <Box width="100%" height="100%" position="absolute">
              <Map
                spots={mapSpots}
                styleOverride={MAPBOX_LIGHT_MAP_STYLE}
                prefetch
                onPrefetchProgress={(done, total) => setLightProgress({ done, total })}
                loaded={() => setLightLoaded(true)}
              />
            </Box>
            <Box width="100%" height="100%" position="absolute">
              <Map
                spots={mapSpots}
                styleOverride={MAPBOX_DARK_MAP_STYLE}
                prefetch
                onPrefetchProgress={(done, total) => setDarkProgress({ done, total })}
                loaded={() => setDarkLoaded(true)}
              />
            </Box>
          </Box>
        </Layout>
      </Layout>
    </Backdrop>
  );
}

export function DownloadExperience({ experience, downloadComplete, downloadError, downloadCanceled }) {
  const { styles } = useTheme();
  const { saveExperience } = useSession();
  const { content } = useLanguage();

  const cancelButtonRef = React.useRef(null);

  const downloadAnnouncement = React.useMemo(() => {
    const template =
      content('aria_download_experience_in_progress') ??
      `${content('aria_download_experience') ?? 'Downloading experience'} {experience}`;
    return template.replace('{experience}', experience?.label ?? '').trim();
  }, [content, experience?.label]);

  useFocusRestore({ isOpen: true, focusRef: cancelButtonRef });

  const [downloadMapTiles, setDownloadMapTiles] = React.useState(null);
  const [progress, setProgress] = React.useState('preparing');
  const [progressPercent, setProgressPercent] = React.useState(null);

  const onPhaseProgress = React.useCallback((completed, total) => {
    setProgressPercent(total ? Math.min(100, Math.round((completed / total) * 100)) : null);
  }, []);

  const download = React.useCallback(
    async (abortSignal) => {
      await sleep(500);
      if (abortSignal.aborted) {
        downloadError(experience);
        return;
      }

      let saved;
      try {
        setProgress('downloading-config');
        saved = await saveExperience(experience, false, { silent: true });
      } catch {
        downloadError(experience);
        return;
      }

      if (!saved?.config || saved?.downloadError) {
        downloadError(saved ?? experience);
        return;
      }

      await sleep(500);
      if (abortSignal.aborted) {
        downloadError(saved);
        return;
      }

      if (saved.config?.audio?.sounds?.length) {
        setProgress('downloading-audio');
        const res = await downloadAudio(saved, onPhaseProgress);
        if (abortSignal.aborted || res?.error) {
          downloadError(saved);
          return;
        }

        await sleep(500);
        if (abortSignal.aborted) {
          downloadError(saved);
          return;
        }
      }

      if (saved.config?.video?.videos?.length) {
        setProgress('downloading-video');
        const res = await downloadVideo(saved, onPhaseProgress);
        if (abortSignal.aborted || res?.error) {
          downloadError(saved);
          return;
        }

        await sleep(500);
        if (abortSignal.aborted) {
          downloadError(saved);
          return;
        }
      }

      if (saved.creditsEnabled && saved.creditsBlocks?.length >= 0) {
        setProgress('downloading-credits');
        const res = await downloadCredits(saved);
        if (abortSignal.aborted || res?.error) {
          downloadError(saved);
          return;
        }

        await sleep(500);
        if (abortSignal.aborted) {
          downloadError(saved);
          return;
        }
      }

      if (configHasGeoSpots(saved.config)) {
        // refuse areas too large to cache offline before spinning up the map
        // (the web counterpart of the native SDK's createPack tile limit)
        const mapSpots = getMapSpotsFromConfig(saved.config);
        const bbox = getBounds(mapSpots.map((spot) => spot.position));
        // check the fitted overview frame (what actually gets cached), not the
        // tight spot bounds — on a tall screen the frame is much larger
        const { frame } = getOverviewFrame(bbox, window.innerWidth, window.innerHeight);
        if (isAreaTooWide(frame)) {
          downloadError({ ...saved, mapAreaTooWide: true });
          return;
        }
        setProgressPercent(null);
        setProgress('downloading-maps');
        setDownloadMapTiles(saved);
      } else downloadComplete(saved);
    },
    [experience]
  );

  const mapLoaded = React.useCallback(async () => {
    await sleep(500);
    downloadComplete(downloadMapTiles);
  }, [downloadMapTiles]);

  const getProgress = React.useCallback(
    (current) => {
      switch (current) {
        case 'preparing':
          return content('download_experience_preparing');
        case 'downloading-config':
          return content('download_experience_config');
        case 'downloading-audio':
          return content('download_experience_audio');
        case 'downloading-video':
          return content('download_experience_video');
        case 'downloading-credits':
          return content('download_experience_credits');
        case 'downloading-maps':
          return content('download_experience_maps');
        default:
          return null;
      }
    },
    [content]
  );

  React.useEffect(() => {
    // keep the screen awake while the modal is open so backgrounding the app
    // (eg. on screen lock) doesn't interrupt long audio/video downloads
    noSleep.enable().catch(() => {});

    const abortController = new AbortController();
    download(abortController.signal);

    return () => {
      abortController.abort();
      noSleep.disable();
    };
  }, []);

  const progressLabel = React.useMemo(() => {
    const label = getProgress(progress);
    const showPercent =
      (progress === 'downloading-audio' ||
        progress === 'downloading-video' ||
        progress === 'downloading-maps') &&
      progressPercent != null;
    return showPercent ? `${label} ${progressPercent}%` : label;
  }, [getProgress, progress, progressPercent]);

  return (
    <>
      <Backdrop
        style={{ position: 'fixed', zIndex: 10, backgroundColor: '#000000aa' }}
        role="presentation"
        open
      >
        <Box
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          textAlign="center"
          p={4}
          style={{
            backgroundColor: styles.palette.background.default,
            borderRadius: '14px',
            width: '80%',
          }}
        >
          <Typography variant="title" mb={2}>
            <String s="download_experience_title" />
          </Typography>
          <Typography variant="text" mb={3}>
            {progressLabel}
          </Typography>
          <Typography id="download-overlay-announcement" className="sr-only" aria-live="assertive">
            {downloadAnnouncement}
          </Typography>
          <Button
            ref={cancelButtonRef}
            color="secondary"
            variant="text"
            onClick={downloadCanceled}
            aria-label={content('aria_cancel_download')}
            aria-describedby="download-overlay-announcement"
          >
            <String s="cancel" />
          </Button>
        </Box>
      </Backdrop>
      {!!downloadMapTiles && (
        <DownloadMapTilesHiddenOverlay
          experience={downloadMapTiles}
          loaded={mapLoaded}
          onProgress={onPhaseProgress}
        />
      )}
    </>
  );
}
